import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import faqData from '../../data/faq-data.json'; 
import Accordion from '../../components/common/Accordion';
import MultiSelectCombobox from '../../components/common/MultiSelectCombobox';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const PublicFaqPage = () => {
  const { faqs, categories } = faqData;
  
  const [selectedCategories, setSelectedCategories] = useState([]); 
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFaqs = useMemo(() => {
    let filtered = faqs;

    if (searchTerm.trim() !== '') {
      const lowercasedTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(faq => 
        (faq.question && faq.question.toLowerCase().includes(lowercasedTerm)) ||
        (faq.answer && faq.answer.toLowerCase().includes(lowercasedTerm))
      );
    }

    if (selectedCategories.length > 0) {
      const selectedCategoryNames = selectedCategories.map(c => c.name);
      filtered = filtered.filter(faq => 
        Array.isArray(faq.categories) && selectedCategoryNames.some(catName => faq.categories.includes(catName))
      );
    }

    return filtered;
  }, [faqs, selectedCategories, searchTerm]);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer.replace(/<[^>]+>/g, '')
      }
    }))
  };

  return (
    <>
      <SEO 
        title="よくある質問（FAQ）｜Stripe請求書一括発行 Billwave"
        description="Stripe請求書CSV一括発行サービス「Billwave」のよくある質問（FAQ）一覧です。料金プラン、機能仕様、銀行振込・クレカ対応、インボイス制度対応、セキュリティ対策など導入前の疑問を解決します。"
        path="/faq"
        jsonLd={faqJsonLd}
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={[{ label: 'よくある質問' }]} />

        <div className="bg-white p-6 sm:p-8 shadow-lg rounded-lg border border-gray-100">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">
            よくある質問（FAQ）
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div>
              <label htmlFor="search-faq" className="block text-sm font-medium text-gray-700 mb-1">キーワード検索</label>
              <input 
                id="search-faq"
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="キーワードを入力..."
                className="w-full px-4 py-2 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-300"
              />
            </div>

            <div>
              <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-1">カテゴリー選択</label>
              <MultiSelectCombobox 
                options={categories} 
                selectedOptions={selectedCategories}
                onChange={setSelectedCategories}
                placeholder="カテゴリーを選択..."
              />
            </div>
          </div>

          <div className="space-y-4">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map(faq => (
                <Accordion key={faq.id} title={faq.question}>
                  <div className="prose max-w-none p-4 text-gray-700" dangerouslySetInnerHTML={{ __html: faq.answer.replace(/\n/g, '<br />') }} />
                  {faq.imageUrl && 
                    <img src={faq.imageUrl} alt={faq.question} loading="lazy" className="mt-2 rounded-lg max-w-sm h-auto mx-auto"/>
                  }
                </Accordion>
              ))
            ) : (
              <div className="text-center text-gray-500 bg-gray-50 p-8 rounded-lg border border-gray-200">
                <p className="text-lg font-medium">該当する質問が見つかりませんでした。</p>
                <p className="text-sm mt-2">検索キーワードやカテゴリを変更して、もう一度お試しください。</p>
              </div>
            )}
          </div>

          <hr className="my-10 border-gray-200" />

          {/* Internal Links & CTA */}
          <section className="bg-blue-50 rounded-xl p-6 text-center border border-blue-100">
            <h2 className="text-xl font-bold text-gray-900 mb-3">解決しない場合はお気軽にお問い合わせください</h2>
            <p className="text-sm text-gray-600 mb-6">
              ご質問やカスタマイズ、ご導入に関するご相談を受け付けております。
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/support/how-to-use" className="bg-white text-blue-600 border border-blue-600 hover:bg-blue-50 font-bold py-2.5 px-6 rounded-lg transition-colors">
                利用ガイドを見る
              </Link>
              <Link to="/contact" className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 font-bold py-2.5 px-6 rounded-lg transition-colors">
                お問い合わせフォーム
              </Link>
              <Link to="/signup" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg transition-colors shadow">
                無料で始める
              </Link>
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default PublicFaqPage;
